/* eslint-disable consistent-return */
/* eslint-disable no-unused-vars */
/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from 'react';
import { find, isEmpty } from 'lodash';
import { Form } from 'react-form';
import moment from 'moment';
import PropTypes from 'prop-types';

import requireAuth from '../hocs/requireAuth';
import { payments } from '../../../constants';
import { required } from '../../utils/form-validation';

import {
    Input,
    Location,
    Textarea,
    CheckBox,
    Select,
    PlayerSearch,
    CollegeSearch,
    InputGroupPre,
} from '../Form';
import InlineRadio from '../Form/InlineRadio';
import RatingChoiceFieldsV2 from './ratingChoiceFieldsV2';
import StripeSetup from '../Stripe/stripeSetup';
import { getClickAttrs } from '../Analytics/gtag';
import { comparator, ComparatorTypes } from '../../utils/comparator';
import { daysOfWeek } from '../../utils/calendar';
import { userIsPaidHitter } from '../../utils/user';
import { checkBirthDate } from '../../utils/validation';
import { RatingChoiceTypeIds } from '../../utils/constants';
import links from '../../../config/links';
import { RESIDENCE_PLACE_ID_FIELD, makeResidenceLocationHandlers } from './residenceLocationHandlers';

const _ = { find };

const ProfileEditForm = (props) => {
    const {
        submitting,
        coreUI,
        profileData,
        months,
        auth,
        availableRegionOptions,
        isSelectRegionDisabled,
    } = props;

    const [isHomeCourtChosenFromDropdown, setIsHomeCourtChosenFromDropdown] = useState(
        !isEmpty(profileData.homeCourt) || isEmpty(profileData.homeClub),
    );
    const [genderValue, setGenderValue] = useState(props.profileData.gender === 'Female' ? 'F' : 'M');
    const [showPlayerSearch, setShowPlayerSearch] = useState(false);
    const [birthDateError, setBirthDateError] = useState(null);

    useEffect(() => {
        if (auth.user.isPaidHitter && !auth.paidHitClub) {
            const paidHitClub = userIsPaidHitter(auth.user);
            const paidHitClubId = paidHitClub.clubId;
            props.fetchPaidHitClub(paidHitClubId);
        }
    }, [auth.user, auth.paidHitClub]);

    const handleHomeCourtChange = () => setIsHomeCourtChosenFromDropdown(false);

    const handleHomeCourtSelect = () => setIsHomeCourtChosenFromDropdown(true);

    // validate that the user changed the region value.
    // this will trigger the warning pop-up message letting the user know that the profile will be locked into the selected region.
    const handleFormSubmit = ({ values }) => {
        setBirthDateError(null);
        // Check if selected region exists in available regions, workaround for Select not clearing default Selection
        const found = availableRegionOptions?.find(r => r.value === parseInt(values.regionid));
        if (!found) { values.regionid = null; }

        const hasBirthDateError = checkBirthDate(values.month, values.year, values.day);
        if (hasBirthDateError) {
            setBirthDateError(hasBirthDateError);
            return;
        }

        props.handleSubmit({ values });
    };

    const { playerProfileAttributes, playerExperienceTypes, coachingExperienceTypes } = coreUI.types;
    const getValues = (attribute) => {
        const attributeObject = _.find(playerProfileAttributes, { name: attribute });
        return attributeObject.values.sort(comparator([{ type: ComparatorTypes.STRING, field: 'value' }]));
    };
    const apparelBrandsOptions = getValues('ApparelBrand');
    const backHandOptions = getValues('Backhand');
    const collegePositionOptions = getValues('CollegePosition');
    const dominantHandOptions = getValues('DominantHand');
    const preferredBallOptions = getValues('PreferredBall');
    const racketBrandOptions = getValues('RacketBrand');
    const shoesBrandOptions = getValues('ShoesBrand');
    let ratingChoiceOptions = getValues('RatingChoice');
    const genderOptions = getValues('Gender');
    const playerExperienceTypeOptions = playerExperienceTypes.sort(comparator([{ type: ComparatorTypes.STRING, field: 'value' }]));
    const coachingExperienceTypeOptions = coachingExperienceTypes.sort(comparator([{ type: ComparatorTypes.STRING, field: 'value' }]));

    // color ball & rating choice display
    const showProgressionBall = (profileData.pbrRatingDisplay || profileData.utrRange?.pbrRange);

    let showRatingChoiceSelect = true;
    if (!showProgressionBall) {
        ratingChoiceOptions = ratingChoiceOptions.filter(ratingChoice => ratingChoice.id !== RatingChoiceTypeIds.PROGRESSION_BALL);
    }
    const allowedRatingChoices = [RatingChoiceTypeIds.UTR, RatingChoiceTypeIds.PROGRESSION_BALL];
    ratingChoiceOptions = ratingChoiceOptions.filter(ratingChoice => allowedRatingChoices.includes(ratingChoice.id));
    if (!showProgressionBall) {
        showRatingChoiceSelect = false;
    }

    const validateHomeCourt = (value) => {
        let error = '';
        const defaultValue = profileData.homeCourt || profileData.homeClub;
        const isHomeCourtValue = !isEmpty(profileData.homeClub) && isEmpty(profileData.homeCourt) && profileData.homeClub === value;
        const isLocationChosenFromDropdown = defaultValue !== value && !(isHomeCourtChosenFromDropdown || isEmpty(value));

        if (isHomeCourtValue && !isHomeCourtChosenFromDropdown) {
            error = 'Please update home court';
        }

        if (isLocationChosenFromDropdown) {
            error = 'Please select location from dropdown';
        }

        return error || null;
    };
    const locationRequired = (value, type) => {
        let error = '';
        let currentValue = '';
        switch (type) {
            case 'nationality':
                currentValue = profileData.locationNationality || profileData.nationality;
                if (currentValue !== value && !props.isNationalityChosenFromDropdown) {
                    error = 'Please select location from dropdown';
                }
                break;
            case 'residence':
                currentValue = profileData.residence;
                if (currentValue !== value && !props.isLocationChosenFromDropdown) {
                    error = 'Please select location from dropdown';
                }
                break;
            default:
                break;
        }
        if (!value) {
            error = 'This field is required.';
        }
        return error || null;
    };

    const handleOnCancelClick = () => {
        setBirthDateError(null);
        props.history.push(`/profiles/${profileData.id}`);
    };

    const hasTennisResults = !!profileData?.hasTennisResults;

    const birthDate = profileData.birthDate ? moment.utc(profileData.birthDate) : null;

    let defaultGender = null;
    if (profileData?.gender === 'Male') {
        defaultGender = 'M';
    }
    if (profileData?.gender === 'Female') {
        defaultGender = 'F';
    }

    const defaultValues = {
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        dominantHand: profileData.dominantHand,
        backhand: profileData.backhand,
        birthPlace: profileData.birthPlace,
        description: profileData.description,
        college: profileData.college,
        heightInches: profileData.heightInches,
        collegePosition: profileData.collegePosition,
        regionid: profileData.playerProfileInformation?.regionId,
        atpOrWtaRank: profileData.atpOrWtaRank,
        racketBrand: profileData.racketBrand,
        racketType: profileData.racketType,
        preferredBall: profileData.preferredBall,
        apparelBrand: profileData.apparelBrand,
        shoesBrand: profileData.shoesBrand,
        shoesType: profileData.shoesType,
        homeCourt: profileData.homeCourt || profileData.homeClub,
        ratingChoice: profileData.ratingChoice,
        residence: profileData.residence,
        [RESIDENCE_PLACE_ID_FIELD]: profileData.residencePlaceId,
        nationality: profileData.locationNationality,
        gender: defaultGender,
        favoritePlayers: profileData.favoritePlayersData || [],
        collegeData: profileData.collegeData,
        day: birthDate ? birthDate.date() : null,
        month: birthDate ? months[birthDate.month()] : null,
        year: birthDate ? birthDate.year() : null,
        playerExperienceTypeId: profileData.playerExperienceTypeId,
        playerExperienceStatus: profileData.playerExperienceStatus,
        coachingExperienceTypeId: profileData.coachingExperienceTypeId,
        coachingExperienceStatus: profileData.coachingExperienceStatus,
        defaultPaidHitPrice: profileData.defaultPaidHitPrice,
        paidHitAvailability: profileData.paidHitAvailability || [],
        showTennisRating: profileData.showTennisRating,
        showPickleballRating: profileData.showPickleballRating,
    };

    const onKeyPress = (event) => {
        if (event.key === 'Enter' &&
            event.target.type !== 'submit' &&
            event.target.type !== 'button' &&
            event.target.id !== 'description' &&
            daysOfWeek.contains(event.target.id)) {
            event.preventDefault();
        }
    };

    const validateFee = value => ({
        error: !value || /^\d+(\.\d{1,2})?$/.test(value) ? null : 'Not a valid fee.',
    });

    let { paymentProvider } = payments;
    if (auth?.paidHitClub?.paymentProviderSetupType.value) {
        paymentProvider = auth.paidHitClub.paymentProviderSetupType.value;
    }
    const activePaymentsEnabled = auth?.paidHitClub?.activePaymentProviderTypes?.find(payment => payment.value === paymentProvider);

    const maxAboutLength = 3000;

    return (
        <Form onSubmit={values => handleFormSubmit({ values })} defaultValues={defaultValues}>
            {(formApi) => {
                const { submitForm, values, addValue, removeValue, setValue } = formApi;

                const {
                    handleChange: handleResidenceChange,
                    handleSelect: handleResidenceSelect,
                } = makeResidenceLocationHandlers(setValue, {
                    onChange: props.handleLocationChange,
                    onSelect: props.handleLocationSelect,
                });

                return (
                    <form onSubmit={submitForm} id="editProfileForm" onKeyPress={onKeyPress}>
                        <div className="container">
                            <div className="row">
                                <div className="col-md-3">
                                    <h5 className={`text-uppercase`}>Profile</h5>
                                </div>
                                <div className="col-md-6 mt56">
                                    <div className={`row`}>
                                        <div className={`col-md-6`}>
                                            <Input
                                                label="First Name"
                                                id="firstName"
                                                data-testid="profile-edit.first-name-input"
                                                field="firstName"
                                                type="text"
                                                placeholder="First Name"
                                                validate={required}
                                            />
                                        </div>
                                        <div className={`col-md-6`}>
                                            <Input
                                                label="Last Name"
                                                id="lastName"
                                                data-testid="profile-edit.last-name-input"
                                                field="lastName"
                                                type="text"
                                                placeholder="Last Name"
                                                validate={required}
                                            />
                                        </div>
                                    </div>
                                    <Location
                                        label="Home Court"
                                        id="homeCourt"
                                        field={'homeCourt'}
                                        data-testid="profile-edit.home-court-input"
                                        inputProps={props.locationInputProps}
                                        handleChange={handleHomeCourtChange}
                                        handleSelect={handleHomeCourtSelect}
                                        useDynamicOption={true}
                                        showMap={false}
                                        placeholder="Home Court"
                                        validate={validateHomeCourt}
                                    />
                                    <RatingChoiceFieldsV2
                                        hasTennisResults={hasTennisResults}
                                        ratingChoiceOptions={ratingChoiceOptions}
                                        showRatingChoiceSelect={showRatingChoiceSelect}
                                        formApi={formApi}
                                    />
                                    <Location
                                        label="Residence"
                                        id="residence"
                                        field={'residence'}
                                        data-testid="profile-edit.residence-input"
                                        inputProps={props.locationInputProps}
                                        handleChange={handleResidenceChange}
                                        handleSelect={handleResidenceSelect}
                                        useDynamicOption={false}
                                        showMap={false}
                                        placeholder="Enter current residence"
                                        validate={value => {
                                            return locationRequired(value, 'residence');
                                        }}
                                        disableProfanityCheck={true}
                                    />
                                    <Select
                                        label="Region"
                                        id="regionid"
                                                data-testid="profile-edit.region-select"
                                        field="regionid"
                                        hiddenDefault={true}
                                        options={availableRegionOptions}
                                        disabled={isSelectRegionDisabled}
                                        hoverText={(
                                            <ul>
                                                <li>Regions are designated by player residence.</li>
                                                <li>In order to select/update a region, you must first select your state of residence.</li>
                                                <li>Players are only able to edit their residence when they are not registered for a current or upcoming event that requires regional play. </li>
                                                <li>Some states have the ability to choose between two regions.</li>
                                                <li>If you are currently registered for an event that has not been completed that requires regional play, you will not be able to change your region. If you need to change you region, you must be withdrawn from the event to register for the correct event.</li>
                                                <li>If your region is locked, you may <a href="https://support.utrsports.net/support/tickets/new" target="_blank" rel="noopener noreferrer" data-testid="profile-edit.region-support-link">contact our support team</a> for assistance.</li>
                                            </ul>

                                        )}
                                        attributeToSave="value"
                                    />
                                    <Input
                                        field={'residence'}
                                        type="hidden"
                                        disableProfanityCheck={true}
                                    />

                                    <Location
                                        label="Nationality"
                                        id="nationality"
                                        field={'nationality'}
                                        data-testid="profile-edit.nationality-input"
                                        inputProps={props.locationInputProps}
                                        handleChange={props.handleNationalityChange}
                                        handleSelect={props.handleNationalitySelect}
                                        useDynamicOption={true}
                                        showMap={false}
                                        placeholder="Enter Country"
                                        validate={value => {
                                            return locationRequired(value, 'nationality');
                                        }}
                                        disableProfanityCheck={true}
                                    />
                                    <Input field={'nationality'} type="hidden" />
                                </div>
                            </div>
                        </div>
                        <div className="container">
                            <div className="row">
                                <div className="col-md-3">
                                    <h5 className={`text-uppercase`}>Player Info</h5>
                                </div>
                                <div className="col-md-6 mt56">
                                    <div className={`row`}>
                                        <div className={`col-md-6`}>
                                            <Select
                                                label="Gender"
                                                id="gender"
                                                field="gender"
                                                data-testid="profile-edit.gender-select"
                                                options={genderOptions}
                                                attributeToSave="value"
                                                validate={required}
                                                onChange={(value, ev) => {
                                                    setGenderValue(value);
                                                }}
                                            />
                                        </div>
                                        <div className={`col-md-6`}>
                                            <Input
                                                label="Height (in inches)"
                                                id="heightInches"
                                                data-testid="profile-edit.height-input"
                                                field="heightInches"
                                                type="number"
                                                placeholder="Height (in inches)"
                                                onKeyPress={(event) => {
                                                    if (!(event.charCode >= 48 && event.charCode <= 57)) {
                                                        event.stopPropagation();
                                                        event.preventDefault();
                                                        return false;
                                                    }
                                                    return true;
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className={`row`}>
                                        <div className="col-12">
                                            <label className="text-dark" data-testid="profile-edit.dob-label">Date of Birth*</label>
                                            <div className="row">
                                                <div className="col-5 col-md-6">
                                                    <Input
                                                        id="month"
                                                        field="month"
                                                        data-testid="profile-edit.dob-month-input"
                                                        type="text"
                                                        validate={required}
                                                        disabled={true}
                                                    />
                                                </div>
                                                <div className="col-3 col-md-3">
                                                    <Input
                                                        id="day"
                                                        field="day"
                                                        data-testid="profile-edit.dob-day-input"
                                                        type="text"
                                                        validate={required}
                                                        disabled={true}
                                                    />
                                                </div>
                                                <div className="col-4 col-md-3">
                                                    <Input
                                                        id="year"
                                                        field="year"
                                                        data-testid="profile-edit.dob-year-input"
                                                        type="text"
                                                        validate={required}
                                                        disabled={true}
                                                    />
                                                </div>
                                                {birthDateError && (
                                                    <div className="col-12">
                                                        <p className="error-text">{birthDateError}</p>
                                                    </div>
                                                )}
                                                <label className="pb24" data-testid="profile-edit.dob-locked-msg">*Contact <a target="_blank" rel="noopener noreferrer" href={links.supportHome} data-testid="profile-edit.dob-locked-msg.support-link">Customer Support</a> to update your date of birth.</label>
                                            </div>
                                        </div>
                                    </div>
                                    <Input
                                        label="Birth Place"
                                        id="birthPlace"
                                                data-testid="profile-edit.birth-place-input"
                                        field="birthPlace"
                                        type="text"
                                        placeholder="Birth Place"
                                    />
                                    <div className={`row`}>
                                        <div className={`col-md-6`}>
                                            <Select
                                                label="Dominant Hand"
                                                id="dominantHand"
                                                data-testid="profile-edit.dominant-hand-select"
                                                field="dominantHand"
                                                options={dominantHandOptions}
                                                attributeToSave="value"
                                            />
                                        </div>
                                        <div className={`col-md-6`}>
                                            <Select
                                                label="Back Hand"
                                                id="backhand"
                                                data-testid="profile-edit.backhand-select"
                                                field="backhand"
                                                options={backHandOptions}
                                                attributeToSave="value"
                                            />
                                        </div>
                                    </div>
                                    <Textarea
                                        label="About"
                                        description="Give a short tennis bio that will appear on your profile."
                                        id="description"
                                                data-testid="profile-edit.description-textarea"
                                        field="description"
                                        rows="5"
                                        placeholder="Ex. Lorem ispum..."
                                        maxLength={maxAboutLength}
                                    />
                                    <p className={'text-right text-small'}>{values.description ? values.description.length : 0}/{maxAboutLength}</p>
                                    <div className={`row`}>
                                        <div className={`col-md-8`}>
                                            <div className={`form-group form-group-xl`}>
                                                <label htmlFor={'college'}>
                                                    {'College Tennis Team'}
                                                </label>
                                                <div className="input-wrapper">
                                                    <CollegeSearch
                                                        className="form-control"
                                                        handleCollegeSelect={(selectedCollege) => {
                                                            return setValue('college', selectedCollege.id);
                                                        }}
                                                        handleInputChange={(val) => {
                                                            if (!val.trim()) {
                                                                return setValue('college', '');
                                                            }
                                                        }}
                                                        placeholder="Search college by name"
                                                        clearAfterSelect={false}
                                                        query={values.collegeData ? values.collegeData.displayName : ''}
                                                        populateQueryOnSelect={true}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className={`col-md-4`}>
                                            <Select
                                                label="Team Position"
                                                id="collegePosition"
                                                data-testid="profile-edit.college-position-select"
                                                field="collegePosition"
                                                options={collegePositionOptions}
                                                attributeToSave="value"
                                            />
                                        </div>
                                    </div>
                                    {genderValue === 'F' ? (
                                        <CheckBox
                                            id="atpOrWtaRank"
                                            field="atpOrWtaRank"
                                            customLabel="Ranked on the WTA tour (current or historical)"
                                            key="female"
                                        />
                                    ) : (
                                        <CheckBox
                                            id="atpOrWtaRank"
                                            field="atpOrWtaRank"
                                            customLabel="Ranked on the ATP tour (current or historical)"
                                            key="male"
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="container">
                            <div className="row">
                                <div className="col-md-3">
                                    <h5 className={`text-uppercase`}>Preferred Gear</h5>
                                </div>
                                <div className="col-md-6 mt56">
                                    <Select
                                        label="Racket Brand"
                                        id="racketBrand"
                                                data-testid="profile-edit.racket-brand-select"
                                        field="racketBrand"
                                        options={racketBrandOptions}
                                        attributeToSave="value"
                                    />
                                    <Input
                                        label="Racket Type"
                                        id="racketType"
                                                data-testid="profile-edit.racket-type-input"
                                        field="racketType"
                                        type="text"
                                        placeholder="Racket Type"
                                    />
                                    <Select
                                        label="Ball"
                                        id="preferredBall"
                                                data-testid="profile-edit.preferred-ball-select"
                                        field="preferredBall"
                                        options={preferredBallOptions}
                                        attributeToSave="value"
                                    />
                                    <Select
                                        label="Apparel Brand"
                                        id="apparelBrand"
                                                data-testid="profile-edit.apparel-brand-select"
                                        field="apparelBrand"
                                        options={apparelBrandsOptions}
                                        attributeToSave="value"
                                    />
                                    <Select
                                        label="Shoes Brand"
                                        id="shoesBrand"
                                                data-testid="profile-edit.shoes-brand-select"
                                        field="shoesBrand"
                                        options={shoesBrandOptions}
                                        attributeToSave="value"
                                    />
                                    <Input
                                        label="Shoes Type"
                                        id="shoesType"
                                        field="shoesType"
                                        data-testid="profile-edit.shoes-type-input"
                                        type="text"
                                        placeholder="Shoes Type"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="container d-none">
                            <div className="row">
                                <div className="col-md-3">
                                    <h5 className={`text-uppercase`}>Favorite Players</h5>
                                </div>
                                <div className="col-md-6 mt10">
                                    {values?.favoritePlayers?.map((player, i) => {
                                        return (
                                            <div className="row mt20" key={`player-${player.id}`}>
                                                <div className="col-11">
                                                    {player.displayName}
                                                </div>
                                                <div className="col-1">
                                                    <span
                                                        className="close-icon"
                                                        onClick={() => removeValue('favoritePlayers', i)}
                                                        data-testid={`profile-edit.remove-favorite-player-btn-${i}`}
                                                    >
                                                        <i className="utr utr-close" />
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {showPlayerSearch && (
                                        <PlayerSearch
                                            className="form-control"
                                            handlePlayerSelect={(selectedPlayer) => {
                                                setShowPlayerSearch(false);
                                                return addValue('favoritePlayers', selectedPlayer);
                                            }}
                                            placeholder="Search player by name"
                                            showInvitePlayer={false}
                                        />
                                    )}
                                    {values.favoritePlayers.length < 3 && !showPlayerSearch && (
                                        <p className="text-small mt24 text-right" data-testid="profile-edit.add-favorite-player-wrapper">
                                            <a
                                                href="#"
                                                className="underline"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setShowPlayerSearch(true);
                                                }}
                                                data-testid="profile-edit.add-favorite-player-link"
                                            >
                                                + Add Favorite Player
                                            </a>
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                        {auth.paidHitClub && (
                            <div className="container">
                                <div className="row">
                                    <div className="col-md-3">
                                        <h5 className={`text-uppercase`} data-testid="profile-edit.paid-hitter.heading">Paid Hitter</h5>
                                    </div>
                                    <div className="col-md-6 mt56">
                                        <div className="row">
                                            <div className="col-9">
                                                <Select
                                                    label="Player Experience (highest level)"
                                                    id="playerExperienceTypeId"
                                                    field="playerExperienceTypeId"
                                                    options={playerExperienceTypeOptions}
                                                    attributeToSave="id"
                                                    data-testid="profile-edit.player-experience"
                                                />
                                            </div>
                                            <div className="col-3">
                                                <InlineRadio
                                                    className={`mt8`}
                                                    options={[
                                                        { value: 'current', label: 'Current' },
                                                        { value: 'former', label: 'Former' },
                                                    ]}
                                                    field="playerExperienceStatus"
                                                    onChange={(e) => {
                                                        return setValue('playerExperienceStatus', e.target.value);
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-9">
                                                <Select
                                                    label="Coaching Experience (highest level)"
                                                    id="coachingExperienceTypeId"
                                                    field="coachingExperienceTypeId"
                                                    options={coachingExperienceTypeOptions}
                                                    attributeToSave="id"
                                                    data-testid="profile-edit.coaching-experience"
                                                />
                                            </div>
                                            <div className="col-3">
                                                <InlineRadio
                                                    className={`mt8`}
                                                    options={[
                                                        { value: 'current', label: 'Current' },
                                                        { value: 'former', label: 'Former' },
                                                    ]}
                                                    field="coachingExperienceStatus"
                                                    onChange={(e) => {
                                                        return setValue('coachingExperienceStatus', e.target.value);
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <InputGroupPre
                                            id="defaultPaidHitPrice"
                                            label="Price of a Paid Hit"
                                            hoverText="This is a default price for a paid hit with you, you can change your price when you create a new paid hit."
                                            field="defaultPaidHitPrice"
                                            type="text"
                                            pre="USD"
                                            data-testid="profile-edit.paid-hit-price"
                                            placeholder="75"
                                            validate={validateFee}
                                        />
                                        <div>
                                            <label data-testid="profile-edit.paid-hit-availability-label">Paid Hit Availability</label>
                                            {daysOfWeek.map((dayOfWeek, i) => {
                                                return (
                                                    <Textarea
                                                        label={<span className="text-capitalize" data-testid={`profile-edit.paid-hit-availability.${dayOfWeek}-label`}>{dayOfWeek}s</span>}
                                                        id={dayOfWeek}
                                                        key={dayOfWeek}
                                                        field={['paidHitAvailability', i, 'availability']}
                                                        rows="2"
                                                        placeholder={`8:00 AM - 10:00 AM\n6:00 PM - 8:00 PM`}
                                                        maxLength="100"
                                                        data-testid={`profile-edit.paid-hit-availability.${dayOfWeek}-input`}
                                                    />
                                                );
                                            })}
                                        </div>
                                        <div className="row">
                                            <StripeSetup
                                                stripeIsSetUp={auth.paidHitClub?.paymentsEnabled}
                                                paymentProvider={paymentProvider}
                                                activePaymentsEnabled={activePaymentsEnabled}
                                                error={null}
                                                isOwner={true}
                                                handleSetupClick={() => {
                                                    props.history.push(`/clubs/${auth.paidHitClub?.id}/payment`);
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="container buttons">
                            <div className="row">
                                <div className="col-md-6 offset-md-3">
                                    <div className="row">
                                        <div className="col-md-6 mb16">
                                            <button
                                                onClick={handleOnCancelClick}
                                                type="button"
                                                className="btn btn-default btn-xl text-uppercase btn-block btn-flex"
                                                data-testid="profile-edit.cancel-btn"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                        <div className="col-md-6 mb16">
                                            <button
                                                type="submit"
                                                className="btn btn-primary btn-xl text-uppercase btn-block btn-flex"
                                                disabled={submitting}
                                                {...getClickAttrs({
                                                    category: 'Profile Edit',
                                                    context: 'Save Profile',
                                                    location: 'Player Profile Edit CTA',
                                                    testid: 'profile-edit.save-btn',
                                                })}
                                            >
                                                {submitting ? 'Saving...' : 'Save'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>
                );
            }}
        </Form>
    );
};

ProfileEditForm.propTypes = {
    auth: PropTypes.instanceOf(Object).isRequired,
};

export default requireAuth(ProfileEditForm);
